import React, { FC } from 'react'
import { SvgExclamationMark } from '@chilipiper/icons/src/design-system'
import { Box, Flex } from '../../new/core-components/box'
import { Text } from '../../new/core-components/text'
import { Icon } from '../icon'
import { Tooltip } from '../tooltip'
import { findInputState, InputState } from './InputState'
import { Label } from './Label'
import { Container } from './Container'
import { EndContent } from './EndContent'
import { StartContent } from './StartContent'
import { Notation } from './Notation'
import { Error } from './Error'
import { FieldProps } from './types'

export const Field: FC<FieldProps> = ({
  labelPlacement = 'inset',
  id,
  hoverColor,
  hideErrorMessage,
  textArea,
  roundedBottomCorners,
  ...p
}: FieldProps) => {
  const inputState = findInputState(p.disabled, p.focused, p.error, p.readOnly)
  const { endContentProps = {} } = p
  const stickyPositioning = { top: p.top, position: p.position }
  return (
    <Box w='100%' h='100%' {...stickyPositioning}>
      {labelPlacement === 'outside' && (
        <Box mb={2}>
          <Text fontWeight={600} color='text/form-label-moderate'>
            {p.label}
          </Text>
          {p.required && (
            <Text ml={1} textStyle='form-label' color='text/action-caution'>
              *
            </Text>
          )}
        </Box>
      )}
      <Container
        {...p}
        roundedBottomCorners={roundedBottomCorners}
        inputState={inputState}
        isFillBgOnHover={p.isFillBgOnHover}
        error={p.error}
        hoverColor={hoverColor}
      >
        <Flex h='100%' w='100%' alignItems='center' position='relative'>
          <StartContent content={p.startContent} />
          <Label
            {...p}
            active={p.focused || Boolean(p.value) || Boolean(p.placeholder)}
            color={p.color}
            id={id}
            inputState={inputState}
            labelStyle={p.fieldStyle}
            label={labelPlacement === 'inset' ? p.label : ''}
            labelPosition={p.labelPosition}
            customStyles={p.labelStyles}
            textArea={textArea}
          >
            {p.children || (
              <Flex h='100%' alignItems='center'>
                <Text color='text/form-placeholder'>{p.placeholder}</Text>
              </Flex>
            )}
          </Label>
          <EndContent {...endContentProps} content={p.endContent} />
          <Notation value={p.notation} />
          {inputState === InputState.Invalid ? (
            <Flex position='relative' height='100%' width='36px' alignItems='center'>
              <Tooltip tip={p.error} disabled={!hideErrorMessage}>
                <Icon
                  position={textArea ? 'absolute' : 'initial'}
                  top={4}
                  color='icon/error'
                  icon={SvgExclamationMark}
                  h={4}
                  w={4}
                />
              </Tooltip>
            </Flex>
          ) : null}
        </Flex>
      </Container>
      {!hideErrorMessage && <Error data-test-id={`Field-error-${id}`} message={p.error} />}
    </Box>
  )
}
